import type { AccountState, UserInfoProps, WithdrawStatus, WithdrawStatusResponse } from './type'
import { isProd } from '@/utils'
import { networks } from '@/utils/config'
import { getBonusApr, getStakerApr, initApi } from '@astar-network/dapp-staking-v3'
import { ApiPromise, WsProvider } from '@polkadot/api'
import axios from 'axios'
import dayjs from 'dayjs'
import { round } from 'lodash'
import { defineStore } from 'pinia'
import { formatUnits } from 'viem'
import { markRaw } from 'vue'
import { useBridgeStore } from './bridge'
import { ContractHelper } from './contract'

const API_BASE = import.meta.env.VITE_WITHDRAW_STATUS_URL || 'https://dev3332.xzsean.eu.org'
export const WAIT_BLOCKS = import.meta.env.VITE_WITHDRAW_WAIT_BLOCK || 129600

export const useAccountStore = defineStore('account', {
  state: (): AccountState => ({
    userInfo: undefined,
    price: 0,
    totalStake: '0',
    apy: '',
    aprBase: '',
    aprBonus: '',
    isVotingPeriod: false,
    stakedAmountOnContract: '0',
    isLoadingApr: true,
    account: '',
    currentNetworkChainId: undefined,
    currentNetworkData: {},
    contractHelper: undefined,
    accountModal: {
      visible: false,
      type: 'wallet',
    },
    pollingTimer: null as NodeJS.Timer | null,
    withdrawStatus: [],
    balanceWithoutWithdraw: 0,
    theLastWithdraw: null,
  }),
  getters: {},
  actions: {
    changeAccount(account?: string) {
      this.account = account
    },
    startPolling() {
      const bridgeStore = useBridgeStore()
      // 清除可能存在的旧定时器
      this.stopPolling()

      // 立即执行一次
      this.getUserInfo()
      this.getWithdrawStatus()
      bridgeStore.getBridgeData()

      // 设置新的定时器
      this.pollingTimer = setInterval(() => {
        if (this.account && this.contractHelper) {
          this.getUserInfo()
          this.getWithdrawStatus()
          bridgeStore.getBridgeData()
        }
      }, 60 * 1000)
    },

    stopPolling() {
      if (this.pollingTimer) {
        clearInterval(Number(this.pollingTimer))
        this.pollingTimer = null
      }
    },

    async getUserInfo() {
      if (this.account && this.contractHelper) {
        console.log(this.contractHelper, 'status')
        const status = await this.contractHelper.getUserInfo(this.account)
        console.log(status, 'status')
        this.userInfo = status as UserInfoProps
      }
    },
    async getApr() {
      // 初始化 Polkadot API
      const wsProvider = new WsProvider(
        isProd ? 'wss://rpc.astar.network' : 'wss://rpc.shibuya.astar.network',
      )
      const api = await ApiPromise.create({ provider: wsProvider })
      // 初始化 dApp staking API
      initApi(api)
      try {
        const contractHelper = this.contractHelper

        this.isLoadingApr = true
        const stakedAmount = await contractHelper?.shibuyaAdapter.getStakedAmountOnContract()
        this.stakedAmountOnContract = stakedAmount
        const apr = await getStakerApr()
        const bonusapr = await getBonusApr()
        console.log(apr, bonusapr, 'aprrr')
        this.apy = Number(stakedAmount) ? String(round(apr + bonusapr.value, 2)) : '-'
        this.aprBase = String(round(apr, 2))
        this.aprBonus = String(round(bonusapr.value, 2))
      }
      catch (error) {
        console.error('Error:', error)
      }
      finally {
        // 无论成功或失败，都要重置加载状态
        this.isLoadingApr = false
        // 清理连接
        await api.disconnect()
      }
    },
    setIsVotingPeriod(isVotingPeriod: boolean) {
      this.isVotingPeriod = isVotingPeriod
    },
    async getTotalStake() {
      if (this.contractHelper) {
        const totalStake = await this.contractHelper.getTotalStake()
        console.log(totalStake, 'totalStake')
        this.totalStake = totalStake
      }
    },
    async getPrice() {
      if (this.contractHelper) {
        const price = await this.contractHelper.getPrice('ASTR')
        console.log(price, 'price')
        this.price = price
      }
    },

    async getWithdrawStatus() {
      if (!this.account) {
        throw new Error('Account not found')
      }

      const { blockNumber: currentBlockNumber } = await this.contractHelper?.getBlockNumber() || { blockNumber: 0 }
      const vaultBalance = await this.contractHelper?.getVaultBalance()

      // 排序，计算出每个withdraw的shortAmount
      const allWithdrawRequest = await this.fetchAllAccountUnWithdraw()
      let accAmount = 0
      const sorted = [...allWithdrawRequest?.data].sort((a, b) => Number(a.rank) - Number(b.rank))
      const allWithdrawRequestWithShortAmount = sorted.map((item) => {
        const amount = Number(formatUnits(BigInt(item.amount), 18))
        accAmount += amount
        return {
          ...item,
          shortAmount: accAmount - Number(vaultBalance?.astrBalance),
        }
      })
      // 计算出所有withdraw的amount，使用vaultBalance减去所有withdraw的amount
      const allWithdrawAmount = allWithdrawRequestWithShortAmount.reduce((acc, item) => acc + Number(item.shortAmount), 0)
      const balanceWithoutWithdraw = Number(vaultBalance?.astrBalance) - allWithdrawAmount
      const theLastWithdraw = allWithdrawRequestWithShortAmount[allWithdrawRequestWithShortAmount.length - 1]

      // const allUnWithdrawRequest = await this.fetchAccountUnWithdraw('0xDA755071bd254e21411429281066E97A65Cbb7dc')
      const allUnWithdrawRequest = await this.fetchAccountUnWithdraw(this.account)

      const withdrawStatus = await Promise.all(
        (allUnWithdrawRequest?.data ?? []).map(async (item) => {
          const amount = Number(formatUnits(BigInt(item.amount), 18))
          // 获取区块时间戳
          const blockTimestampRes = await this.contractHelper?.getBlockTimestamp(Number(item.block_number))
          // 找到 rank 相同的项
          const found = allWithdrawRequestWithShortAmount.find(u => Number(u.rank) === Number(item.rank))
          return {
            ...item,
            amount: formatUnits(BigInt(item.amount), 18),
            hash: `${item.sender}_${item.nonce}`,
            currentBlockNumber,
            expectBlockNumber: Number(item.block_number) + Number(WAIT_BLOCKS),
            isReadyByBlockNumber: currentBlockNumber > Number(item.block_number) + Number(WAIT_BLOCKS),
            shortAmount: found ? found.shortAmount : amount - Number(vaultBalance?.astrBalance),
            blockTimestamp: blockTimestampRes?.timestamp || 0,
            blockTime: dayjs(blockTimestampRes?.timestamp ? blockTimestampRes.timestamp * 1000 : 0).format('YYYY-MM-DD HH:mm:ss'),
          }
        }),
      )
      this.withdrawStatus = withdrawStatus || []
      this.balanceWithoutWithdraw = balanceWithoutWithdraw
      this.theLastWithdraw = theLastWithdraw
      return this.withdrawStatus // 返回结果供调用者使用
    },

    setNetwork(chainId: number) {
      this.currentNetworkChainId = chainId
      this.currentNetworkData = networks.find(item => item.id === chainId) || {}
      this.contractHelper = markRaw(ContractHelper.getInstance(chainId))
    },
    toggleModalVisible() {
      this.accountModal.visible = !this.accountModal.visible
    },
    changeModal(type: 'wallet' | 'history') {
      this.accountModal.type = type
    },
    async getTxStatus(hash: string, type: string) {
      if (this.account && this.contractHelper) {
        const status = await this.contractHelper.getTxStatus(hash, this.account, type)
        return status
      }
    },
    async fetchAccountUnWithdraw(userAddress: string): Promise<WithdrawStatusResponse | null> {
      try {
        const res = await axios.get(`${API_BASE}/api/accountUnWithdraw/${userAddress}`)
        return res.data as WithdrawStatusResponse
      }
      catch (e) {
        console.error('Failed to fetch unwithdrawn orders for user', e)
        return null
      }
    },
    async fetchAllAccountUnWithdraw() {
      try {
        const res = await axios.get(`${API_BASE}/api/allAccountInfo`)
        return res.data
      }
      catch (e) {
        console.error('Failed to fetch all unwithdrawn orders', e)
        return null
      }
    },
    async fetchAccountAllRequest(userAddress: string): Promise<WithdrawStatus | null> {
      try {
        const res = await axios.get(`${API_BASE}/api/accountAllRequest/${userAddress}`)
        return res.data as WithdrawStatus
      }
      catch (e) {
        console.error('Failed to fetch all orders for user', e)
        return null
      }
    },
  },
})
